import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ImagePlus, RefreshCw, Save } from "lucide-react";
import { SITE_IMAGE_FILES, from, rpc, unwrap, useAction, useHasRole, useOrg } from "@vixio/data";
import {
  Button, Callout, Checkbox, DataTable, NumberInput, PageHeader, Panel, StatusPill, TextArea, TextInput, formatDateTime,
} from "@vixio/ui";
import type { SiteSetting } from "./api";
import { MediaUploadDialog, siteMediaPath } from "./media";
import {
  REASON_LABEL, REBUILD_HOOK_KEY, SITE_SETTING_FIELDS, SITE_SETTING_GROUPS, validateSiteSetting, type SiteBuild, type SiteSettingField, type SiteStatus,
} from "./site";

// ---------------------------------------------------------------- situação do site

const BUILD_LABEL: Record<string, { label: string; tone: "ok" | "warn" | "crit" | "info" | "neutral" }> = {
  pending: { label: "na fila", tone: "info" },
  triggering: { label: "pedindo a atualização", tone: "info" },
  triggered: { label: "site atualizando", tone: "info" },
  live: { label: "atualizado", tone: "ok" },
  failed: { label: "falhou", tone: "crit" },
  unconfirmed: { label: "sem confirmação", tone: "warn" },
};

export function useSiteStatus() {
  const org = useOrg();
  return useQuery({
    queryKey: ["content", "site-status", org.id],
    queryFn: () => unwrap<SiteStatus[]>(from("content", "v_site_status").select("*").eq("org_id", org.id)).then((r) => r[0] ?? null),
    // Enquanto há atualização aberta, a tela acompanha.
    refetchInterval: (q) => (q.state.data?.current_status ? 15_000 : false),
  });
}

/** "Site atualizando / atualizado em …" — no topo das páginas do site e nas configurações. */
export function SiteStatusLine({ compact }: { compact?: boolean }) {
  const s = useSiteStatus();
  const canPublish = useHasRole("manager");
  const org = useOrg();
  const now = useAction(() => rpc("content", "rebuild_site", { p_org: org.id }), { invalidate: [["content"]], success: "Atualização do site pedida" });
  const st = s.data;
  if (!st) return null;
  if (!st.hook_configured || !st.hook_enabled) {
    return (
      <Callout tone="warn" title="O site não se atualiza sozinho">
        Publicar aqui só aparece no site no próximo deploy manual. Configure o gancho de atualização em{" "}
        <Link to="/conteudo/configuracoes-do-site">Configurações do site</Link>.
      </Callout>
    );
  }
  const cur = st.current_status;
  const reasons = [...new Set((st.current_reasons ?? []).map((r) => REASON_LABEL[r] ?? r))].join(", ");
  return (
    <div className="vx-row" style={{ gap: 10, alignItems: "center" }} aria-live="polite">
      {cur ? (
        <>
          <StatusPill tone="info">{BUILD_LABEL[cur]?.label ?? cur}</StatusPill>
          <span className="text-2">
            {cur === "pending" && st.current_due_at ? `sai às ${formatDateTime(st.current_due_at).slice(-5)}` : null}
            {cur === "triggered" && st.current_triggered_at ? `pedida às ${formatDateTime(st.current_triggered_at).slice(-5)}; o build leva alguns minutos` : null}
            {!compact && reasons ? ` · ${st.current_changes ?? 1} ${st.current_changes === 1 ? "mudança" : "mudanças"} (${reasons})` : null}
            {st.current_error ? ` · última tentativa: ${st.current_error}` : null}
          </span>
        </>
      ) : st.last_status === "failed" || st.last_status === "unconfirmed" ? (
        <>
          <StatusPill tone={BUILD_LABEL[st.last_status]!.tone}>{BUILD_LABEL[st.last_status]!.label}</StatusPill>
          <span className="text-2">{st.last_error ?? "o build não confirmou em 30 minutos"}</span>
        </>
      ) : (
        <StatusPill tone="ok">site em dia</StatusPill>
      )}
      {st.live_at ? <span className="text-2">atualizado em {formatDateTime(st.live_at)}</span> : null}
      {canPublish ? (
        <Button size="sm" variant="ghost" icon={<RefreshCw />} loading={now.isPending} onClick={() => now.mutate(undefined)}>
          Atualizar agora
        </Button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- configurações (site.*)

function toText(v: unknown): string {
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  return "";
}

function SettingsGroup({ group, fields, rows }: { group: string; fields: SiteSettingField[]; rows: SiteSetting[] }) {
  const org = useOrg();
  const canEdit = useHasRole("admin");
  const current = useMemo(() => Object.fromEntries(rows.map((r) => [r.key, r.value])), [rows]);
  const [values, setValues] = useState<Record<string, string | number | null>>({});
  const [uploadFor, setUploadFor] = useState<SiteSettingField | null>(null);
  useEffect(() => {
    setValues(Object.fromEntries(fields.map((f) => [f.key, f.type === "number" ? (typeof current[f.key] === "number" ? (current[f.key] as number) : null) : toText(current[f.key])])));
  }, [current, fields]);
  const errors = Object.fromEntries(fields.map((f) => [f.key, values[f.key] === undefined ? null : validateSiteSetting(f, typeof values[f.key] === "string" ? (values[f.key] as string).trim() : values[f.key])]));
  const changed = fields.filter((f) => values[f.key] !== undefined && JSON.stringify(values[f.key]) !== JSON.stringify(f.type === "number" ? current[f.key] ?? null : toText(current[f.key])));
  const save = useAction(
    async () => {
      for (const f of changed) {
        const v = values[f.key];
        await rpc("content", "set_setting", { p_org: org.id, p_key: f.key, p_value: typeof v === "string" ? v.trim() : v, p_category: "site", p_is_public: true });
      }
      return changed.length;
    },
    { invalidate: [["content"]], success: (n) => `${n} ${n === 1 ? "configuração salva" : "configurações salvas"}: o site atualiza na próxima publicação` },
  );
  return (
    <Panel
      title={SITE_SETTING_GROUPS[group as SiteSettingField["group"]] ?? group}
      footer={
        canEdit ? (
          <Button variant="primary" icon={<Save />} loading={save.isPending} disabled={!changed.length || changed.some((f) => errors[f.key])} onClick={() => save.mutate(undefined)}>
            Salvar {changed.length ? `(${changed.length})` : ""}
          </Button>
        ) : (
          <span className="text-2">Só administradores alteram as configurações do site.</span>
        )
      }
    >
      <div className="vx-stack">
        {fields.map((f) =>
          f.type === "number" ? (
            <NumberInput key={f.key} label={f.label} hint={f.help} value={(values[f.key] as number | null) ?? null} onChange={(n) => setValues((v) => ({ ...v, [f.key]: n }))} min={1} max={10000} disabled={!canEdit} error={errors[f.key] ?? undefined} />
          ) : f.type === "textarea" ? (
            <TextArea key={f.key} label={f.label} hint={f.help} value={(values[f.key] as string) ?? ""} onChange={(t) => setValues((v) => ({ ...v, [f.key]: t }))} rows={3} maxLength={f.max} disabled={!canEdit} error={errors[f.key] ?? undefined} />
          ) : (
            <div key={f.key} className="vx-stack" style={{ gap: 6 }}>
              <TextInput
                label={f.label}
                hint={f.help ?? (current[f.key] === undefined ? "Ainda sem valor no painel: o site usa o padrão." : undefined)}
                value={(values[f.key] as string) ?? ""}
                onChange={(t) => setValues((v) => ({ ...v, [f.key]: t }))}
                maxLength={f.max ?? 500}
                inputMode={f.type === "whatsapp" || f.type === "phone" ? "tel" : f.type === "email" ? "email" : undefined}
                disabled={!canEdit}
                error={errors[f.key] ?? undefined}
              />
              {f.type === "path" && canEdit ? (
                <div>
                  <Button size="sm" icon={<ImagePlus />} onClick={() => setUploadFor(f)}>
                    Enviar imagem
                  </Button>
                </div>
              ) : null}
            </div>
          ),
        )}
      </div>
      {uploadFor ? (
        <MediaUploadDialog
          rule={SITE_IMAGE_FILES}
          title={`Enviar imagem: ${uploadFor.label}`}
          onClose={() => setUploadFor(null)}
          onUploaded={(m) => setValues((v) => ({ ...v, [uploadFor.key]: siteMediaPath(m.path) }))}
        />
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------- gancho de atualização

interface Hook {
  url: string;
  secret_ref: string | null;
  check_url: string | null;
  debounce_seconds: number;
  enabled: boolean;
}

function HookPanel({ hook }: { hook: Hook | null }) {
  const org = useOrg();
  const canEdit = useHasRole("admin");
  const [form, setForm] = useState<Hook>({ url: "", secret_ref: "COOLIFY_SITE_TOKEN", check_url: "https://vixio.com.br/build.json", debounce_seconds: 120, enabled: true });
  useEffect(() => {
    if (hook) setForm({ ...hook, secret_ref: hook.secret_ref ?? "", check_url: hook.check_url ?? "" });
  }, [hook]);
  const save = useAction(
    () =>
      rpc("content", "set_rebuild_hook", {
        p_org: org.id,
        p_url: form.url.trim(),
        p_secret_ref: form.secret_ref?.trim() || null,
        p_check_url: form.check_url?.trim() || null,
        p_debounce_seconds: form.debounce_seconds,
        p_enabled: form.enabled,
      }),
    { invalidate: [["content"]], success: "Gancho de atualização salvo" },
  );
  return (
    <Panel
      title="Gancho de atualização do site"
      footer={
        canEdit ? (
          <Button variant="primary" icon={<Save />} loading={save.isPending} disabled={!/^https:\/\//.test(form.url.trim())} onClick={() => save.mutate(undefined)}>
            Salvar gancho
          </Button>
        ) : (
          <span className="text-2">Só administradores alteram o gancho.</span>
        )
      }
    >
      <div className="vx-stack">
        <p className="text-2">
          Publicar no painel pede ao Coolify um novo deploy do site, que é estático e lê o conteúdo no build. No Coolify: aplicação do site →
          Webhooks → “Deploy Webhook” (endereço <span className="mono">https://…/api/v1/deploy?uuid=…</span>). O token de API do Coolify (Keys &amp;
          Tokens, permissão de deploy) fica na variável do worker indicada abaixo — nunca no endereço.
        </p>
        <TextInput label="Endereço do webhook de deploy" value={form.url} onChange={(url) => setForm((f) => ({ ...f, url }))} placeholder="https://coolify.exemplo.com/api/v1/deploy?uuid=..." disabled={!canEdit} required />
        <TextInput
          label="Segredo do token (nome da variável no worker)"
          hint="Ex.: COOLIFY_SITE_TOKEN, definida nas variáveis do worker no Coolify. Quando o cofre de segredos estiver pronto, use vault:<chave>."
          value={form.secret_ref ?? ""}
          onChange={(secret_ref) => setForm((f) => ({ ...f, secret_ref }))}
          disabled={!canEdit}
        />
        <TextInput
          label="Endereço de conferência (build.json do site)"
          hint="O worker lê este arquivo depois do pedido: quando a data do build muda, a tela mostra “atualizado em …”."
          value={form.check_url ?? ""}
          onChange={(check_url) => setForm((f) => ({ ...f, check_url }))}
          disabled={!canEdit}
        />
        <NumberInput
          label="Intervalo mínimo entre atualizações"
          hint="Publicações dentro do intervalo entram na mesma atualização."
          value={form.debounce_seconds}
          onChange={(n) => setForm((f) => ({ ...f, debounce_seconds: n ?? 120 }))}
          min={30}
          max={3600}
          step={30}
          suffix="segundos"
          disabled={!canEdit}
        />
        <Checkbox label="Atualizar o site a cada publicação" checked={form.enabled} onChange={(enabled) => setForm((f) => ({ ...f, enabled }))} disabled={!canEdit} />
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------- página

export function SiteSettingsPage() {
  const org = useOrg();
  const rows = useQuery({
    queryKey: ["content", "settings", org.id],
    queryFn: () => unwrap<SiteSetting[]>(from("content", "site_settings").select("*").eq("org_id", org.id).order("key")),
  });
  const builds = useQuery({
    queryKey: ["content", "site-builds", org.id],
    queryFn: () => unwrap<SiteBuild[]>(from("content", "v_site_builds").select("*").eq("org_id", org.id).order("requested_at", { ascending: false }).limit(10)),
  });
  const hook = (rows.data ?? []).find((r) => r.key === REBUILD_HOOK_KEY)?.value as Hook | undefined;
  const groups = useMemo(() => {
    const g = new Map<string, SiteSettingField[]>();
    for (const f of SITE_SETTING_FIELDS) g.set(f.group, [...(g.get(f.group) ?? []), f]);
    return [...g.entries()];
  }, []);
  return (
    <div className="vx-content">
      <PageHeader
        title="Configurações do site"
        description="Contatos, redes, marca, logo e SEO do site público, e a atualização automática ao publicar. Textos de cada página ficam em Conteúdo → Site."
      />
      <div className="vx-section vx-stack vx-stack--lg">
        <Panel title="Situação do site">
          <SiteStatusLine />
        </Panel>
        <div className="vx-grid vx-grid--2">
          {groups.map(([group, fields]) => (
            <SettingsGroup key={group} group={group} fields={fields} rows={rows.data ?? []} />
          ))}
        </div>
        <HookPanel hook={hook ?? null} />
        <Panel title="Últimas atualizações">
          <DataTable<SiteBuild>
            caption="Atualizações do site"
            bare
            data={builds.data ?? []}
            rowKey={(b) => b.id}
            empty="Nenhuma atualização pedida ainda."
            columns={[
              { key: "requested_at", header: "Primeira mudança", kind: "datetime" },
              { key: "status", header: "Situação", cell: (b) => <StatusPill tone={BUILD_LABEL[b.status]?.tone ?? "neutral"}>{BUILD_LABEL[b.status]?.label ?? b.status}</StatusPill> },
              { key: "changes", header: "Mudanças", kind: "number" },
              { key: "reasons", header: "O quê", cell: (b) => [...new Set(b.reasons.map((r) => REASON_LABEL[r] ?? r))].join(", ") },
              { key: "triggered_at", header: "Pedida ao Coolify", kind: "datetime" },
              { key: "live_at", header: "No ar", kind: "datetime" },
              { key: "last_error", header: "Erro", cell: (b) => b.last_error ?? "" },
            ]}
          />
        </Panel>
      </div>
    </div>
  );
}
